/* eslint-disable no-console */
import { Image } from '@rsa-digital/evo-shared-components/commonTypes/image';
import {
  CallToAction,
  TextLink,
} from '@rsa-digital/evo-shared-components/commonTypes/links';
import {
  VideoType as SimpleVideo,
  VideoCookiePolicyData,
} from '@rsa-digital/evo-shared-components/commonTypes/video';
import {
  Collapse,
  Heading,
} from '@rsa-digital/evo-shared-components/components/InfoBanner';
import {
  ContentRow,
  HeaderRow,
  SimpleTableProps,
} from '@rsa-digital/evo-shared-components/components/Table/SimpleTable';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import { BreadcrumbsData } from 'components/Breadcrumbs/types';
import { Locale, MetaTags } from 'components/PageMetadata';
import { ReadMoreLink } from 'components/blocks/RelatedContent/types';
import {
  CsAsset,
  CsConfirmationModal,
  CsCta,
  CsEntryNode,
  CsInfoBanner,
  CsTable,
  CsVideo,
  PageMeta,
} from 'types/contentStack';
import { buildWarning } from './errorReporting';
import { handleAndTrackLinkClick } from './navigation';
import { getVideoCookiePolicyData } from './videos';

const forceNoIndex = process.env.GATSBY_FORCE_NOINDEX;
/**
 * Returns undefined if the object if contains no truthy values,
 * otherwise returns the original object.
 *
 * For optional fields in contentstack that contain multiple fields, if
 * the user leaves the types blank then we get an object with empty values,
 * rather than null/undefined. For example, this occurs when dealing with
 * the 'link' type being used for optional CTAs. We can use this method
 * to process those cases.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const undefinedIfEmpty = <T extends Record<string, any>>(
  csObject: T | null
): T | undefined => {
  if (csObject != null && Object.values(csObject).some((v) => !!v)) {
    return csObject;
  }
  return undefined;
};

/**
 * Returns the single element in the array if it exists, otherwise returns undefined
 *
 * For certain fields in contentstack, if the user leaves it blank then we get an
 * empty array, and when filled, we get a singleton array. One such field is the
 * reference field. We can use this method to process those cases.
 */
export const unwrapSingleton = <T>(singletonOrEmptyArray: [T] | []): T | undefined =>
  first(singletonOrEmptyArray);

type Asset = {
  publicUrl: string;
  filename?: string;
  description?: string;
  height?: number;
  width?: number;
};

export const processAsset = (csAsset: CsAsset | null): Asset | undefined => {
  const asset = undefinedIfEmpty(csAsset);

  /* istanbul ignore if */
  if (asset && !asset.description) {
    buildWarning(`Asset with filename "${asset.filename}" is missing a description`);
  }

  return asset
    ? {
        publicUrl: asset.localAsset?.publicURL ?? asset.url,
        filename: asset.filename ?? /* istanbul ignore next */ undefined,
        description: asset.description ?? /* istanbul ignore next */ undefined,
        width: asset.dimension?.width ?? /* istanbul ignore next */ undefined,
        height: asset.dimension?.height ?? /* istanbul ignore next */ undefined,
      }
    : undefined;
};

export const processImageAsset = (csAsset: CsAsset | null): Image | undefined => {
  const asset = processAsset(csAsset);

  return asset
    ? {
        url: asset.publicUrl,
        altText: asset.description ?? /* istanbul ignore next */ undefined,
        height: asset.height,
        width: asset.width,
      }
    : undefined;
};

export const processMandatoryImage = (image: CsAsset | null | undefined): Image => {
  const processedImage = processImageAsset(image ?? null);
  if (processedImage) {
    return processedImage;
  }
  throw new Error('Mandatory image missing');
};

export const processFavicon = (url: string): Image => {
  const altText = 'RSA logo';
  const height = 256;
  const width = 256;

  return { url, altText, height, width };
};

/**
 * openInNewTab is being passed to handleAndTrackLinkClick and then
 * handleLinkClick as the param suppressNavigation. For the CTA the
 * event target is the span rather than the <a> so a check on event.target
 * will not work.
 *
 * Needs to remain as openInNewTab as this naming convention is being used
 * at the component level in the component library to handle displaying
 * the appropriate icon.
 */
export const processCta = (cta: CsCta, trackingStyle: string): CallToAction => ({
  displayText: cta.display_text,
  screenReaderText: cta.screen_reader_text,
  url: cta.url,
  openInNewTab: cta.open_in_new_tab,
  onClick: handleAndTrackLinkClick(
    cta.url,
    trackingStyle,
    cta.screen_reader_text || cta.display_text,
    cta.open_in_new_tab
  ),
});

export const processOptionalCta = (
  maybeCta: [CsCta] | [] | null,
  trackingStyle: string
): CallToAction | undefined => {
  if (isEmpty(maybeCta)) {
    return undefined;
  }

  const cta = maybeCta?.[0] as CsCta;
  return processCta(cta, trackingStyle);
};

export const processMandatoryCta = (
  cta: [CsCta],
  trackingStyle: string
): CallToAction => {
  const result = processOptionalCta(cta, trackingStyle);
  /* istanbul ignore next */
  if (result === undefined) {
    throw new Error('Missing CTA field');
  }
  return result;
};

export type VideoWithJsonLd = {
  url: string;
  screenReaderText: string;
  jsonLd: string;
  videoCookiePolicyData?: VideoCookiePolicyData;
};

export const processOptionalVideo = (
  maybeVideo: [CsVideo] | [] | null
): VideoWithJsonLd | undefined => {
  if (isEmpty(maybeVideo)) {
    return undefined;
  }

  const video = maybeVideo?.[0] as CsVideo;
  /* istanbul ignore if */
  if (video && !video.screen_reader_text) {
    buildWarning(`Video with src "${video.url}" is missing a description`);
  }

  return {
    url: video.url,
    screenReaderText: video.screen_reader_text ?? /* istanbul ignore next */ '',
    jsonLd: video.json_ld,
    videoCookiePolicyData: getVideoCookiePolicyData(video.url),
  };
};

export const processMandatoryVideo = (video: [CsVideo] | null): VideoWithJsonLd => {
  if (isEmpty(video)) {
    throw new Error('Missing Video field');
  }

  const unwrappedVideo = video?.[0] as CsVideo;
  /* istanbul ignore if */
  if (unwrappedVideo && !unwrappedVideo.screen_reader_text) {
    buildWarning(`Video with src "${unwrappedVideo.url}" is missing a description`);
  }

  return {
    url: unwrappedVideo.url,
    screenReaderText: unwrappedVideo.screen_reader_text ?? /* istanbul ignore next */ '',
    jsonLd: unwrappedVideo.json_ld,
    videoCookiePolicyData: getVideoCookiePolicyData(unwrappedVideo.url),
  };
};

export const processSimpleVideo = (video: [CsVideo] | null): SimpleVideo => {
  const result = processMandatoryVideo(video);

  return {
    src: result.url,
    screenReaderText: result.screenReaderText,
    videoCookiePolicyData: getVideoCookiePolicyData(result.url),
  };
};

export const processTable = (table: CsTable): SimpleTableProps => {
  const headerRow: HeaderRow = {
    hasCornerHeadingGap: table.header_row.has_empty_corner_cell,
    cornerCellColumnSpan: table.header_row.corner_cell_column_span ?? 1,
    headerCells: table.header_row.header_cells.map((header_cell) => ({
      headingText: header_cell.heading_text,
      columnSpan: header_cell.column_span,
    })),
  };

  const contentRows: ContentRow[] = table.content_rows.map((content_row) => ({
    contentRow: {
      cells: content_row.content_row.cells.map((cell) => ({
        cell: {
          contentRichText: cell.cell.content_rich_text,
          columnSpan: cell.cell.column_span,
          isHighlighted: cell.cell.is_highlighted,
        },
      })),
    },
  }));
  return { table: { headerRow, contentRows } };
};

export const processReadMoreLink: (
  readMore: ReadMoreLink | undefined
) => TextLink | undefined = (readMore) =>
  readMore && readMore.text && readMore.url
    ? {
        text: readMore.text,
        url: readMore.url,
        openInNewTab: readMore.open_in_new_tab,
      }
    : undefined;

const processRobots = (robots: string | undefined): string | undefined => {
  // This is used to hide pages from search engines in non-production environments
  if (forceNoIndex === 'true') {
    return 'noindex, nofollow';
  }
  return robots;
};

const processCanonical = (pageUrl: string): string | undefined => {
  // When hiding pages we should not include a canonical tag
  // https://www.reddit.com/r/TechSEO/comments/8yahdr/2_questions_about_the_canonical_tag/e2dey9i/?context=1

  if (forceNoIndex === 'true') {
    return undefined;
  }
  return process.env.GATSBY_SITE_URL + pageUrl;
};

const processLocale = (csLocale: CsEntryNode): Locale => ({
  localeCode: csLocale.locale,
  canonicalUrl: csLocale.page_meta.canonical_tag,
});

export const processPageMeta = (
  pageMeta: PageMeta,
  pageUrl: string,
  csLocaleInfo?: CsEntryNode[],
  defaultOgType?: string,
  fallbackOgImage?: CsAsset | null
): { breadcrumbs: BreadcrumbsData; meta: MetaTags } => ({
  breadcrumbs: pageMeta.breadcrumbs,
  meta: {
    title: pageMeta.title,
    robots: processRobots(pageMeta.robots),
    description: pageMeta.description,
    canonical_tag: processCanonical(pageUrl),
    og_url: pageMeta.og_url || pageMeta.canonical_tag || pageUrl,
    og_title: pageMeta.og_title || pageMeta.title,
    og_description: pageMeta.og_description || pageMeta.description,
    og_image:
      processImageAsset(pageMeta.og_image) || processImageAsset(fallbackOgImage || null),
    og_type: pageMeta.og_type || defaultOgType || '',
    twitter_site: pageMeta.twitter_site,
    twitter_card: pageMeta.twitter_card || 'summary',
    localeInfo: csLocaleInfo?.map(processLocale) || [],
  },
});

export const processInfoBannerData = (
  infoBanner: CsInfoBanner
): {
  collapse?: Collapse;
  heading?: Heading;
} => ({
  collapse: infoBanner.collapsible
    ? {
        showText: infoBanner.collapsible.show_text,
        hideText: infoBanner.collapsible.hide_text,
        displayInitially: infoBanner.collapsible.display_initially,
      }
    : undefined,
  heading: infoBanner.heading.title
    ? {
        text: infoBanner.heading.title,
        icon: infoBanner.heading.icon ? infoBanner.heading.icon[0].icon_code : undefined,
      }
    : undefined,
});

export const processConfirmationModalData = (
  confirmationModal: CsConfirmationModal
): { title: string; body: string } | undefined => {
  if (
    confirmationModal.modal_body &&
    confirmationModal.modal_body !== null &&
    confirmationModal.modal_title &&
    confirmationModal.modal_title !== null
  ) {
    return {
      title: confirmationModal.modal_title,
      body: confirmationModal.modal_body,
    };
  }
  return undefined;
};
